import { LTV } from 'app/features/util/ltv';
import { SiloAsset } from 'app/queries/decoratedSilo';
import { useMemo } from 'react';
import { getDisplayedMax, serializeAmount } from 'app/hooks/useMaxTokenAmount';
import { getLtvLimit } from './getLtvLimit';
import { BigNumberWithDecimals, createBigNumberWithDecimals } from '@silo/types';

type UseMaxValues = (params: {
  currentLTV: LTV | null;
  selectedAsset: SiloAsset;
  setLendingAmount: (amount: string) => void;
  setProtectedAmount: (amount: string) => void;
}) => {
  maxLendingAmount: MaxLendingAmount;
  maxProtectedAmount: MaxProtectedAmount;
  doLendingMax: () => void;
  doProtectedMax: () => void;
};

export type MaxProps = ReturnType<UseMaxValues>;

export const useMaxValues: UseMaxValues = ({ currentLTV, selectedAsset, setLendingAmount, setProtectedAmount }) => {
  const maxLendingAmount = useMemo(
    () => getMaxLendingAmount({ currentLTV, selectedAsset }),
    [currentLTV, selectedAsset]
  );
  const maxProtectedAmount = useMemo(
    () => getMaxProtectedAmount({ currentLTV, selectedAsset }),
    [currentLTV, selectedAsset]
  );

  const doLendingMax = () => {
    const displayedMax = getDisplayedMax(maxLendingAmount);

    setLendingAmount(serializeAmount(displayedMax));
  };

  const doProtectedMax = () => {
    const displayedMax = getDisplayedMax(maxProtectedAmount);

    setProtectedAmount(serializeAmount(displayedMax));
  };

  return {
    maxLendingAmount,
    maxProtectedAmount,
    doLendingMax,
    doProtectedMax,
  };
};

export type MaxLendingAmount = {
  type: 'protocolBalance' | 'ltvLimit' | 'liquidity';
  value: BigNumberWithDecimals;
};

type GetMaxLendingAmount = (params: { currentLTV: LTV | null; selectedAsset: SiloAsset }) => MaxLendingAmount;

const getMaxLendingAmount: GetMaxLendingAmount = ({
  currentLTV,
  selectedAsset: { collateralBalance, liquidity, decimals, assetAddress },
}) => {
  const ltvLimit = getLtvLimit(currentLTV, assetAddress);

  if (ltvLimit?.bigNumber.lt(collateralBalance) && ltvLimit?.bigNumber.lt(liquidity)) {
    return {
      type: 'ltvLimit',
      value: ltvLimit,
    };
  }

  if (liquidity.lt(collateralBalance)) {
    return {
      type: 'liquidity',
      value: createBigNumberWithDecimals(liquidity, decimals),
    };
  }

  return {
    type: 'protocolBalance',
    value: createBigNumberWithDecimals(collateralBalance, decimals),
  };
};

export type MaxProtectedAmount = {
  type: 'protocolBalance' | 'ltvLimit';
  value: BigNumberWithDecimals;
};

type GetMaxProtectedAmount = (params: { currentLTV: LTV | null; selectedAsset: SiloAsset }) => MaxProtectedAmount;

const getMaxProtectedAmount: GetMaxProtectedAmount = ({
  currentLTV,
  selectedAsset: { collateralOnlyBalance, decimals, assetAddress },
}) => {
  const ltvLimit = getLtvLimit(currentLTV, assetAddress);

  if (ltvLimit?.bigNumber.lt(collateralOnlyBalance)) {
    return {
      type: 'ltvLimit',
      value: ltvLimit,
    };
  }

  return {
    type: 'protocolBalance',
    value: createBigNumberWithDecimals(collateralOnlyBalance, decimals),
  };
};
